import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { budgetFromFit, deriveOverflowLayout } from './overflowLayout';
const DEFAULT_OVERFLOW_BTN_WIDTH = 100;
export function useOverflowLayout(items, activeId, options = {}) {
    var _a;
    const btnWidth = (_a = options.overflowButtonWidth) !== null && _a !== void 0 ? _a : DEFAULT_OVERFLOW_BTN_WIDTH;
    const containerRef = useRef(null);
    const itemRefs = useRef(new Map());
    const widthCache = useRef(new Map());
    const gapRef = useRef(0);
    const n = items.length;
    // `budget` = number of item-slots the row shows. null = not yet computed / all
    // items fit (no "More" button). A value < n means overflow.
    const [budget, setBudget] = useState(null);
    const setItemRef = useCallback((id) => (el) => {
        if (el)
            itemRefs.current.set(id, el);
        else
            itemRefs.current.delete(id);
    }, []);
    // Remeasure only when the *set* of items changes, not on every new array
    // reference — parents often pass a freshly-built array each render/navigation.
    const itemsKey = useMemo(() => items.map(i => i.id).join('|'), [items]);
    // The only DOM-measuring step. Collapsed items are display:none, so force them
    // visible to read their natural width. Item wrappers are flex-shrink:0 flex
    // children, so widths stay natural even with all of them expanded at once and
    // forcing display:block is harmless. Callers run this before paint.
    const measure = useCallback(() => {
        const container = containerRef.current;
        if (!container)
            return;
        gapRef.current = parseFloat(getComputedStyle(container).columnGap) || 0;
        const pairs = items
            .map(i => [i.id, itemRefs.current.get(i.id)])
            .filter((p) => !!p[1]);
        pairs.forEach(([, el]) => { el.style.display = 'block'; });
        pairs.forEach(([id, el]) => {
            if (el.offsetWidth > 0)
                widthCache.current.set(id, el.offsetWidth);
        });
        pairs.forEach(([, el]) => { el.style.removeProperty('display'); });
    }, [items]);
    // Cheap, cache-only: compute how many item-slots fit. Width-only — promotion is
    // derived below, so this doesn't need the active item.
    const recompute = useCallback(() => {
        var _a;
        const container = containerRef.current;
        const wrapperWidth = (_a = container === null || container === void 0 ? void 0 : container.offsetWidth) !== null && _a !== void 0 ? _a : 0;
        if (!container || wrapperWidth === 0)
            return;
        const gap = gapRef.current;
        const btnW = btnWidth; // rough floor; the verify pass corrects any residual clip
        const widthOf = (id) => { var _a; return (_a = widthCache.current.get(id)) !== null && _a !== void 0 ? _a : 0; };
        // offsetWidth excludes flex gaps — count them separately per element pair.
        let total = 0;
        for (const it of items)
            total += widthOf(it.id);
        if (total + Math.max(n - 1, 0) * gap <= wrapperWidth) {
            setBudget(n);
            return;
        } // all fit
        // Natural fit count `f`: showing items [0..i] plus the button is i+2
        // elements → i+1 gaps.
        let f = n;
        let acc = 0;
        for (let i = 0; i < n; i++) {
            if (acc + widthOf(items[i].id) + (i + 1) * gap + btnW > wrapperWidth) {
                f = i;
                break;
            }
            acc += widthOf(items[i].id);
        }
        setBudget(budgetFromFit(f));
    }, [items, n, btnWidth]);
    // Measure (before paint) whenever the set of items changes, then split.
    useLayoutEffect(() => { measure(); recompute(); }, [itemsKey]); // eslint-disable-line react-hooks/exhaustive-deps
    // On selection change, reset budget to the width estimate (so verify reductions
    // made for a previous active item don't accumulate); the verify pass re-tightens.
    useLayoutEffect(() => { recompute(); }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps
    // Late web-font swaps change widths — remeasure once fonts settle.
    useEffect(() => {
        var _a, _b;
        let cancelled = false;
        (_b = (_a = document.fonts) === null || _a === void 0 ? void 0 : _a.ready) === null || _b === void 0 ? void 0 : _b.then(() => { if (!cancelled) {
            measure();
            recompute();
        } });
        return () => { cancelled = true; };
    }, [itemsKey]); // eslint-disable-line react-hooks/exhaustive-deps
    // Recompute on any width change of the wrapper (window resize, sibling items
    // growing/shrinking us).
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        const observer = new ResizeObserver(recompute);
        observer.observe(container);
        return () => observer.disconnect();
    }, [recompute]);
    // Safety net: the width estimate can come up short (the active item is bold and
    // wider than measured, late fonts, sub-pixel rounding). Both wrappers are
    // overflow:hidden with flex-shrink:0 children, so any shortfall clips the
    // trailing "More" button off the edge. If the row actually overflows, shrink the
    // budget by one and re-check next commit. Because promotion is derived from
    // budget, a smaller budget re-promotes the active item instead of stranding it.
    // Returns the same budget when nothing needs doing so React bails and it converges.
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container || container.scrollWidth <= container.clientWidth + 1)
            return;
        setBudget(prev => {
            const cur = prev === null ? n : prev;
            if (cur <= 0)
                return prev; // nothing left to collapse
            const next = cur - 1;
            return next <= 1 ? 0 : next; // never rest at 1 (≤1 → collapse fully)
        });
    }, [budget, activeId, n]);
    // Derive the concrete layout from budget + active index (pure fn). Any budget
    // change (recompute or verify) re-runs this, so the active item is never stranded.
    const derived = useMemo(() => deriveOverflowLayout(items, budget, activeId), [items, budget, activeId]);
    const isHidden = useCallback((id) => derived.hiddenIds.has(id), [derived]);
    return {
        containerRef,
        setItemRef,
        orderedItems: derived.orderedItems,
        isHidden,
        overflowItems: derived.overflowItems,
        promotedId: derived.promotedId,
        hasOverflow: derived.overflowItems.length > 0,
        visibleCount: derived.visibleCount,
    };
}
